import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "@/hooks/use-business";
import type { Tables } from "@/integrations/supabase/types";
import { startOfMonth, endOfMonth } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { isNativeIosApp } from "@/lib/platform";

type CampaignRow = Tables<"marketing_campaigns">;

export function useMarketingCampaigns() {
  const { business } = useBusiness();
  const { toast } = useToast();
  const isNativeIos = isNativeIosApp();
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaigns = useCallback(async () => {
    if (!business) {
      setCampaigns([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from("marketing_campaigns")
        .select("*")
        .eq("business_id", business.id)
        .order("created_at", { ascending: false });
      if (err) throw err;
      setCampaigns(data || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, [business]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const enforceCampaignLimit = async (type: "email" | "sms") => {
    if (!business) throw new Error("No business found");

    const planId = (business as any).plan_id || "starter";
    const { data: plan, error: planError } = await supabase
      .from("subscription_plans")
      .select("sms_campaign_limit_monthly, email_campaign_limit_monthly")
      .eq("id", planId)
      .maybeSingle();
    if (planError) throw planError;

    const lim =
      type === "sms"
        ? (plan as any)?.sms_campaign_limit_monthly ?? null
        : (plan as any)?.email_campaign_limit_monthly ?? null;

    if (lim == null) return;

    const now = new Date();
    const monthStart = startOfMonth(now).toISOString();
    const monthEnd = endOfMonth(now).toISOString();

    const { data: existing, error: countError } = await supabase
      .from("marketing_campaigns")
      .select("id")
      .eq("business_id", business.id)
      .eq("type", type)
      .in("status", ["sent", "scheduled"])
      .gte("created_at", monthStart)
      .lte("created_at", monthEnd);
    if (countError) throw countError;

    const current = existing?.length || 0;
    if (current >= lim) {
      toast({
        title: "Campaign limit reached",
        description: isNativeIos
          ? "Please contact Buukd support for help sending more campaigns."
          : `Your plan allows up to ${lim} ${type.toUpperCase()} campaigns per month. Upgrade to send more.`,
        variant: "destructive",
      });
      throw new Error("Campaign limit reached");
    }
  };

  const createCampaign = async (payload: Omit<CampaignRow, "id" | "created_at" | "updated_at">) => {
    if (!business) throw new Error("No business found");

    // Friendly client-side limit check (DB triggers also enforce)
    const status = payload.status as any;
    const type = payload.type as any;
    if ((status === "sent" || status === "scheduled") && (type === "sms" || type === "email")) {
      await enforceCampaignLimit(type);
    }

    const { data, error: err } = await supabase
      .from("marketing_campaigns")
      .insert(payload as any)
      .select()
      .single();
    if (err) {
      const msg = (err as any)?.message || "Failed to save campaign";
      if (msg.toLowerCase().includes("campaign limit reached")) {
        toast({
          title: "Campaign limit reached",
          description: isNativeIos
            ? "Please contact Buukd support for help sending more campaigns."
            : "Upgrade your plan to send more campaigns this month.",
          variant: "destructive",
        });
      }
      throw err;
    }
    await fetchCampaigns();
    return data as CampaignRow;
  };

  const updateCampaign = async (id: string, patch: Partial<CampaignRow>) => {
    // Friendly client-side limit check when sending/scheduling a draft
    const nextStatus = patch.status as any;
    if (nextStatus === "sent" || nextStatus === "scheduled") {
      const { data: current, error: currentErr } = await supabase
        .from("marketing_campaigns")
        .select("type, status")
        .eq("id", id)
        .maybeSingle();
      if (currentErr) throw currentErr;
      const currentStatus = (current as any)?.status;
      const type = (current as any)?.type;
      if ((currentStatus !== "sent" && currentStatus !== "scheduled") && (type === "sms" || type === "email")) {
        await enforceCampaignLimit(type);
      }
    }

    const { data, error: err } = await supabase
      .from("marketing_campaigns")
      .update(patch as any)
      .eq("id", id)
      .select()
      .single();
    if (err) {
      const msg = (err as any)?.message || "Failed to update campaign";
      if (msg.toLowerCase().includes("campaign limit reached")) {
        toast({
          title: "Campaign limit reached",
          description: isNativeIos
            ? "Please contact Buukd support for help sending more campaigns."
            : "Upgrade your plan to send more campaigns this month.",
          variant: "destructive",
        });
      }
      throw err;
    }
    await fetchCampaigns();
    return data as CampaignRow;
  };

  return { campaigns, loading, error, refetch: fetchCampaigns, createCampaign, updateCampaign };
}


